import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, Wallet, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { playClickSound } from '@/utils/soundUtils';
import { Button, QuickAmountChips, AmountStepper } from '@/components/ui';
import APP_CONFIG from '@/config/appConfig';
import { typeRole, fsClass } from '@/styles/typography';

interface GameProvider {
    id: string;
    name: string;
    icon: string;
}

interface OrderSummaryModalProps {
    isOpen: boolean;
    onClose: () => void;
    totalAmount: number;
    providerAmounts: Map<string, number>;
    providers: GameProvider[];
    onIncrement: (providerId: string) => void;
    onDecrement: (providerId: string) => void;
    onRemoveItem: (providerId: string) => void;
    selectedProvider?: GameProvider | null;
    selectedAction?: 'deposit' | 'withdrawal' | null;
    onConfirm?: () => void;
    /**
     * Cosmos cart mode: when provided, rows render QuickAmountChips + AmountStepper
     * with absolute amounts instead of the legacy +/- controls.
     */
    onAmountChange?: (providerId: string, amount: number) => void;
    /** Per-provider max deposit (falls back to 10000). Only used with onAmountChange. */
    providerMaxAmounts?: Map<string, number>;
}

const OrderSummaryModal: React.FC<OrderSummaryModalProps> = ({
    isOpen,
    onClose,
    totalAmount,
    providerAmounts,
    providers,
    onIncrement,
    onDecrement,
    onRemoveItem,
    selectedProvider,
    selectedAction,
    onConfirm,
    onAmountChange,
    providerMaxAmounts,
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    if (!isOpen) return null;

    // Check if there are any items in the order
    const hasItems = Array.from(providerAmounts.values()).some(amount => amount > 0);
    const stepAmount = APP_CONFIG.jumpingEachClick || 5;

    const handleConfirmDeposit = () => {
        playClickSound();

        // Save provider amounts to sessionStorage
        const amountsArray = Array.from(providerAmounts.entries())
            .filter(([_, amount]) => amount > 0)
            .map(([providerId, amount]) => ({ providerId, amount }));

        if (amountsArray.length > 0) {
            const pendingData = {
                amounts: amountsArray,
                timestamp: Date.now(),
                source: 'confirm_deposit'
            };

            try {
                sessionStorage.setItem('pending_deposit_amounts', JSON.stringify(pendingData));
                console.log('💾 Saved pending deposit amounts to sessionStorage:', pendingData);
            } catch (error) {
                console.error('Failed to save to sessionStorage:', error);
            }
        }

        // Close popup
        onClose();

        // Call custom confirm handler if provided
        if (onConfirm) {
            onConfirm();
            return;
        }

        // Navigate to login page with provider and action info
        const params = new URLSearchParams();
        if (selectedProvider) {
            params.set('provider', String(selectedProvider.id));
            params.set('providerName', selectedProvider.name);
        }
        if (selectedAction) {
            params.set('action', selectedAction);
        }
        if (totalAmount > 0) {
            params.set('totalAmount', String(totalAmount));
        }
        navigate(`/login?${params.toString()}`);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-3 sm:px-4">
            <div
                className="absolute inset-0 cosmos-modal-backdrop"
                aria-hidden
            />

            <div
                className={`cosmos-modal-panel relative z-50 flex flex-col overflow-hidden w-full max-h-[var(--wf-mdl-max-h)] min-h-0 safe-area-bottom ${hasItems ? '' : 'sm:min-h-[var(--wf-mdl-empty-min-h)]'}`}
                style={{
                    width: hasItems ? 'var(--wf-mdl-withdraw-w)' : 'var(--wf-mdl-w)',
                    maxWidth: 'var(--wf-mdl-viewport-max)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="relative flex flex-col items-center px-4 pt-4 pb-2.5 gap-1.5 flex-shrink-0">
                    <h3 className={`font-display text-ink text-center font-bold ${typeRole.header2} leading-tight uppercase tracking-[0.12em]`}>
                        {t('orderSummary.title', 'Add Funds Cart')}
                    </h3>
                    <div className="cosmos-beam w-16 h-0.5 rounded-full shrink-0" />
                    <button
                        type="button"
                        onClick={() => { playClickSound(); onClose(); }}
                        className="absolute right-2.5 top-2.5 p-1.5 rounded-control-sm text-body-muted hover:text-ink hover:bg-white/10 transition-colors"
                        aria-label={t('common.close', 'Close')}
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Scrollable cart rows */}
                <div className={`flex flex-col w-full ${hasItems ? 'cosmos-scroll-y px-3 sm:px-4 pt-0 pb-2 sm:pb-3 flex-1 min-h-0' : ''}`}>
                    {hasItems ? (
                        Array.from(providerAmounts.entries())
                            .filter(([_, amount]) => amount > 0)
                            .map(([providerId, amount], index, array) => {
                                const provider = providers.find(p => p.id === providerId);
                                if (!provider) return null;
                                const isLast = index === array.length - 1;
                                const maxAmount = providerMaxAmounts?.get(providerId) ?? 10000;

                                return (
                                    <div key={providerId} className="w-full">
                                        <div className="flex flex-col w-full py-2.5 sm:py-3 gap-2">
                                            {/* Row header: icon + name + amount + remove */}
                                            <div className="flex items-center justify-between w-full gap-2">
                                                <div className="flex items-center flex-1 min-w-0 gap-2">
                                                    <div className="lobby-gi-ico flex justify-center items-center w-8 h-8 rounded-control-sm shrink-0 overflow-hidden">
                                                        <img
                                                            src={provider.icon}
                                                            alt={provider.name}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.src = '/icons/egame.jpg';
                                                            }}
                                                        />
                                                    </div>
                                                    <p className={`font-ui text-ink font-semibold ${typeRole.caption} truncate min-w-0`}>
                                                        {provider.name}
                                                    </p>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    {onAmountChange ? (
                                                        <span className={`font-money font-semibold ${typeRole.body} text-money-out tabular-nums`}>
                                                            ${Math.round(amount).toFixed(2)}
                                                        </span>
                                                    ) : (
                                                        /* Legacy +/- controls (callers without onAmountChange) */
                                                        <div className="flex items-center gap-1">
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    playClickSound();
                                                                    onDecrement(providerId);
                                                                }}
                                                                disabled={amount <= 0}
                                                                className="flex justify-center items-center w-7 h-7 rounded-control-sm border border-border-ice bg-white/5 text-ice hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                                aria-label={t('orderSummary.decrease', 'Decrease amount')}
                                                            >
                                                                <Minus className="w-3 h-3" />
                                                            </button>
                                                            <span className={`min-w-[60px] text-center font-money font-semibold ${typeRole.caption} text-money-out tabular-nums`}>
                                                                ${Math.round(amount).toFixed(2)}
                                                            </span>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    playClickSound();
                                                                    onIncrement(providerId);
                                                                }}
                                                                disabled={amount >= 10000}
                                                                className="flex justify-center items-center w-7 h-7 rounded-control-sm border border-border-ice bg-white/5 text-ice hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                                                                aria-label={t('orderSummary.increase', 'Increase amount')}
                                                            >
                                                                <Plus className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    )}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            playClickSound();
                                                            onRemoveItem(providerId);
                                                        }}
                                                        className="shrink-0 w-7 h-7 flex items-center justify-center rounded-control-sm text-body-muted hover:text-danger-soft hover:bg-danger/15 transition-colors"
                                                        aria-label={t('orderSummary.remove', 'Remove {{name}} from cart', { name: provider.name })}
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Per-row amount editing: quick chips + stepper */}
                                            {onAmountChange && (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <QuickAmountChips
                                                        value={amount}
                                                        onSelect={(amt) => {
                                                            playClickSound();
                                                            onAmountChange(providerId, Math.min(amt, maxAmount));
                                                        }}
                                                    />
                                                    <AmountStepper
                                                        value={amount}
                                                        onChange={(next) => onAmountChange(providerId, next)}
                                                        step={stepAmount}
                                                        min={0}
                                                        max={maxAmount}
                                                        format={(v) => `$${Math.round(v).toFixed(2)}`}
                                                        className={`[&>button]:w-8 [&>button]:h-8 [&>span]:min-w-[84px] [&>span]:${typeRole.body}`}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {!isLast && <div className="w-full h-px bg-border-ice/50" />}
                                    </div>
                                );
                            })
                    ) : (
                        /* Empty State */
                        <div className="flex flex-col items-center w-full px-4 pb-3 gap-0 min-h-0 cosmos-scroll-y">
                            <div className="flex flex-col items-center gap-3 w-full max-w-[var(--wf-wallet-payout-w)]">
                                <div className="flex justify-center items-center w-16 h-16 sm:w-20 sm:h-20 rounded-full border border-border-ice bg-info/5 shrink-0">
                                    <Wallet className="w-8 h-8 sm:w-10 sm:h-10 text-info-soft/80" />
                                </div>
                                <p className={`font-display text-ink text-center font-bold ${typeRole.caption} uppercase tracking-[0.18em]`}>
                                    {t('orderSummary.emptyTitle', 'No games selected')}
                                </p>
                                <p className={`font-ui text-center text-body-muted ${typeRole.caption} leading-snug max-w-[var(--wf-wallet-payout-w)]`}>
                                    {t('orderSummary.emptyHint', 'Go back and select some games to fund your adventure.')}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Total */}
                <div className="w-full flex flex-row justify-between items-center px-4 py-2.5 sm:py-3 flex-shrink-0 border-t border-border-ice bg-white/[0.04]">
                    <p className={`font-ui ${typeRole.caption} font-semibold uppercase tracking-[0.18em] text-caption-muted`}>
                        {t('orderSummary.total', 'Total Deposit')}
                    </p>
                    <p className={`font-money font-semibold ${typeRole.header2} tabular-nums ${hasItems ? 'text-money-out' : 'text-caption-muted'}`}>
                        ${Math.round(totalAmount).toFixed(2)}
                    </p>
                </div>

                {/* Footer buttons */}
                <div className="flex flex-row justify-center items-center gap-2 px-4 py-3 flex-shrink-0 w-full">
                    <Button
                        variant="tertiary"
                        size="md"
                        onClick={() => {
                            playClickSound();
                            onClose();
                        }}
                    >
                        {t('common.close', 'Close')}
                    </Button>
                    <Button
                        variant="moneyIn"
                        size="md"
                        fullWidth
                        className="flex-1"
                        disabled={!hasItems}
                        onClick={handleConfirmDeposit}
                    >
                        {t('orderSummary.continueToCheckout', 'Continue to Checkout')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default OrderSummaryModal;
